<script setup>
import { computed, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import PortalSessionRow from './PortalSessionRow.vue';
import { timeState } from '../../helpers/sessionTimes';

/**
 * Home's "Coming up" (XN-4566a, XN-4558): the next few sessions from the
 * members' `/my/sessions` entries. Order: the ones on now, then the ones
 * starting within 15 minutes, then the ones the member has a seat in, then
 * the soonest. Each is the same row as on Sessions (XN-4745): its details open
 * in place, one at a time, and Join opens the call on Sessions, which hosts
 * the room. A seat saved or a session gone in a panel goes up to Home, which
 * holds the list. With no rows the block is absent, not an empty card
 * (XN-4734). Prototype `.sh` + `.ev` (build 29 Sep).
 */
const props = defineProps({
  sessions: { type: Array, required: true },
  now: { type: Date, required: true },
  timeZone: { type: String, required: true },
});
const emit = defineEmits(['seat', 'gone']);

const MAX_ROWS = 3;
const live = computed(() => props.sessions
  .map((entry) => ({ entry, ...timeState(entry, props.now) }))
  .filter(({ ended }) => !ended)
  .sort((a, b) => (b.live - a.live) || (b.joinable - a.joinable) || (Number(Boolean(b.entry.seat)) - Number(Boolean(a.entry.seat)))
    || (Date.parse(a.entry.starts_at) - Date.parse(b.entry.starts_at)))
  .slice(0, MAX_ROWS)
  .map(({ entry }) => entry));

// While a row is open the order is frozen at what it was on opening, so a seat
// saved or released in its panel cannot move the row (or drop it) under the
// member's hands. The entries stay live; the order resumes on close.
const openId = ref(null);
let frozen = [];
const toggle = (id) => {
  if (openId.value === null) frozen = live.value.map((entry) => entry.id);
  openId.value = openId.value === id ? null : id;
};
const rows = computed(() => (openId.value === null
  ? live.value
  : frozen.map((id) => props.sessions.find((entry) => entry.id === id))
    .filter((entry) => entry && !timeState(entry, props.now).ended)));
// The open occurrence ended or left the list (a series rolls to its next date
// under a new id): resume the live order so the replacement shows.
watch([() => props.sessions, () => props.now], () => {
  if (openId.value === null) return;
  const open = props.sessions.find((entry) => entry.id === openId.value);
  if (!open || timeState(open, props.now).ended) openId.value = null;
});
const gone = (id) => {
  if (openId.value === id) openId.value = null;
  emit('gone', id);
};
</script>

<template>
  <section v-if="rows.length" class="coming" data-testid="coming-up">
    <div class="coming__sh">
      <h2>Coming up</h2>
      <span class="coming__ln" />
      <RouterLink to="/hub/sessions" data-testid="all-sessions">All sessions</RouterLink>
    </div>
    <PortalSessionRow
      v-for="entry in rows"
      :key="entry.id"
      :session="entry"
      :now="now"
      :time-zone="timeZone"
      :open="openId === entry.id"
      @toggle="toggle"
      @seat="emit('seat', $event)"
      @gone="gone"
    />
  </section>
</template>

<style scoped>
/* Prototype `.sh` (build 29 Sep 15:06); the rows are PortalSessionRow's. */
.coming__sh { display: flex; align-items: baseline; gap: 12px; margin: 34px 0 14px; }
.coming__sh h2 {
  margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--xl-color-outline-variant);
}
.coming__ln { flex: 1; height: 1px; background: var(--xl-color-border); }
.coming__sh a { font-size: 13.5px; font-weight: 600; color: var(--xl-color-primary); text-decoration: none; }
</style>
